import { useEffect, useState } from 'react';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { tr } from '../../lib/i18n';
import { numeroLisible } from '../../lib/telephone';
import { C } from '../../theme/palette';

/** Longueur du code reçu par e-mail. */
const LONGUEUR_CODE = 6;

/**
 * Le code de connexion à six chiffres, par e-mail ou par SMS.
 *
 * <p>Quand SwissTech l'exige de tous (le réglage du serveur par défaut), la
 * carte ne propose plus d'interrupteur : elle dit où arrive le code, et laisse
 * enregistrer le numéro de portable qui le reçoit par SMS, contre le mot de
 * passe. Sinon, elle garde l'activation au choix de chacun :
 *
 * <p>Activer demande le mot de passe, puis un premier code reçu : c'est la
 * preuve que l'adresse fonctionne, sans quoi la personne s'enfermerait dehors
 * à sa prochaine connexion. Désactiver demande le mot de passe seul.
 *
 * <p>L'ouvrier aussi entre par son adresse et le code : la carte vaut pour lui.
 *
 * @returns {JSX.Element | null} la carte, ou rien sans session
 */
export function DoubleAuthCard() {
  const { session } = useSession();
  // `null` tant que le serveur n'a pas répondu : la carte ne dit rien qu'elle
  // ne sache.
  const [active, setActive] = useState(null);
  const [obligatoire, setObligatoire] = useState(false);
  const [telephone, setTelephone] = useState('');
  const [nouveauNumero, setNouveauNumero] = useState('');
  const [smsDisponible, setSmsDisponible] = useState(false);
  // Une application d'authentification reliée tient lieu de code : sa carte
  // le dit, et ce que celle-ci annoncerait (« un code t'est envoyé ») serait faux.
  const [application, setApplication] = useState(false);
  // Le code par e-mail éteint dans Réglages → Mon compte → Ta 2ᵉ sécurité.
  const [emailEteint, setEmailEteint] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [defi, setDefi] = useState('');
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState('');
  const [info, setInfo] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // Retirer le numéro se confirme : sans lui, le SMS n'est plus proposé.
  const [retraitAConfirmer, setRetraitAConfirmer] = useState(false);
  const sansSession = !session;

  useEffect(() => {
    if (sansSession) return undefined;
    let annule = false;
    authApi
      .etatDoubleAuth()
      .then((etat) => {
        if (annule) return;
        setActive(Boolean(etat?.active));
        setObligatoire(Boolean(etat?.obligatoire));
        setTelephone(etat?.telephone || '');
        setSmsDisponible(Boolean(etat?.smsDisponible));
        setApplication(Boolean(etat?.application));
        setEmailEteint(etat?.email === false);
      })
      .catch(() => {
        if (!annule) setErreur('L’état de la double authentification n’a pas pu être lu.');
      });
    return () => {
      annule = true;
    };
  }, [sansSession]);

  if (sansSession) return null;
  if (application && !obligatoire) return null;

  /** Exécute une commande en affichant l'échec éventuel. */
  const tente = async (action) => {
    setErreur('');
    setInfo('');
    setEnvoi(true);
    try {
      await action();
    } catch (e) {
      setErreur(e?.message || 'La demande n’a pas abouti.');
    } finally {
      setEnvoi(false);
    }
  };

  const demandeActivation = () =>
    tente(async () => {
      const reponse = await authApi.activeDoubleAuth(motDePasse);
      setDefi(reponse.defi);
      setMotDePasse('');
      setCode('');
      setInfo(`Un code vient d’être envoyé à ${session.email}. Saisis-le pour confirmer.`);
    });

  const confirme = () =>
    tente(async () => {
      await authApi.confirmeDoubleAuth(defi, code);
      setActive(true);
      setDefi('');
      setCode('');
      setInfo('Double authentification activée : un code te sera demandé à chaque connexion.');
    });

  const desactive = () =>
    tente(async () => {
      await authApi.desactiveDoubleAuth(motDePasse);
      setActive(false);
      setMotDePasse('');
      setInfo('Double authentification désactivée. Le mot de passe seul suffit de nouveau.');
    });

  const etat = active === null ? '…' : active ? 'Activée' : 'Désactivée';

  // Un champ vide retire le numéro enregistré : le serveur le comprend ainsi.
  const retrait = Boolean(telephone) && !nouveauNumero.trim();

  const enregistreNumero = () =>
    tente(async () => {
      const etatNouveau = await authApi.changeTelephoneCode(motDePasse, nouveauNumero.trim());
      setTelephone(etatNouveau?.telephone || '');
      setMotDePasse('');
      setNouveauNumero('');
      setRetraitAConfirmer(false);
      setInfo(
        etatNouveau?.telephone
          ? 'Numéro enregistré : tu pourras recevoir le code par SMS.'
          : 'Numéro retiré : le code arrivera par e-mail.',
      );
    });

  if (obligatoire) {
    return (
      <Card style={{ marginBottom: 12 }}>
        <Bar
          icon="🔐"
          title="Code de connexion à 6 chiffres"
          color={C.teal}
          right={
            <span style={{ fontSize: 9, fontWeight: 800, color: C.green }}>Toujours actif</span>
          }
        />
        <div style={{ padding: '13px 15px', fontSize: 12, lineHeight: 1.6, color: C.ink }}>
          {emailEteint ? (
            <div style={{ marginBottom: 10 }}>
              {tr(
                '✅ À chaque connexion, après ton mot de passe, un code à 6 chiffres t’est demandé. Le code par e-mail est éteint : tes moyens se règlent dans Réglages → Mon compte.',
              )}
            </div>
          ) : (
            <div style={{ marginBottom: 10 }}>
              ✅ À chaque connexion, après ton mot de passe, tu choisis de recevoir un code à 6
              chiffres par e-mail à <b>{session.email}</b>
              {smsDisponible ? (
                telephone ? (
                  <>
                    {' '}
                    ou par SMS au <b>{numeroLisible(telephone)}</b>.
                  </>
                ) : (
                  '. Ajoute ton numéro de portable pour pouvoir le recevoir aussi par SMS.'
                )
              ) : (
                '. Le SMS n’est pas encore raccordé : le code arrive par e-mail.'
              )}
            </div>
          )}
          {smsDisponible && (
            <>
              <Inp
                label={telephone ? 'Nouveau numéro de portable' : 'Numéro de portable'}
                placeholder="+41 79 123 45 67"
                inputMode="tel"
                value={nouveauNumero}
                onChange={(e) => setNouveauNumero(e.target.value)}
              />
              <Inp
                label="Mot de passe actuel, pour confirmer"
                type="password"
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
              />
              {retraitAConfirmer ? (
                <div
                  role="group"
                  aria-label="Confirmer le retrait du numéro"
                  style={{ background: C.redL, borderRadius: 9, padding: '10px 12px' }}
                >
                  <div style={{ fontSize: 11, color: C.ink, marginBottom: 8, lineHeight: 1.5 }}>
                    Retirer le numéro <b>{numeroLisible(telephone)}</b> ? Le code de connexion ne
                    pourra plus arriver par SMS, seulement par e-mail.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn
                      label={envoi ? 'Un instant…' : 'Oui, retirer le numéro'}
                      color={C.red}
                      full
                      disabled={envoi}
                      onClick={enregistreNumero}
                    />
                    <Btn
                      label="Annuler"
                      ghost
                      color={C.grey}
                      onClick={() => setRetraitAConfirmer(false)}
                    />
                  </div>
                </div>
              ) : (
                <Btn
                  label={
                    envoi ? 'Un instant…' : retrait ? 'Retirer le numéro' : 'Enregistrer le numéro'
                  }
                  color={retrait ? C.red : C.teal}
                  full
                  disabled={envoi || !motDePasse || (!retrait && !nouveauNumero.trim())}
                  onClick={retrait ? () => setRetraitAConfirmer(true) : enregistreNumero}
                />
              )}
              {retrait && !retraitAConfirmer && (
                <div style={{ fontSize: 9.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                  Laisse la case vide pour retirer le numéro enregistré, ou écris le nouveau.
                </div>
              )}
            </>
          )}
          {erreur && (
            <div role="alert" style={{ fontSize: 10, color: C.red, fontWeight: 700, marginTop: 8 }}>
              {erreur}
            </div>
          )}
          {info && (
            <div
              role="status"
              style={{ fontSize: 10, color: C.green, fontWeight: 700, marginTop: 8 }}
            >
              {info}
            </div>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="📧"
        title="Double authentification par e-mail"
        color={C.teal}
        right={
          <span style={{ fontSize: 9, fontWeight: 800, color: active ? C.green : C.muted }}>
            {etat}
          </span>
        }
      />
      <div style={{ padding: '13px 15px', fontSize: 12, lineHeight: 1.6, color: C.ink }}>
        {active === true && !defi && (
          <>
            <div style={{ marginBottom: 10 }}>
              ✅ <b>Activée.</b> À chaque connexion, un code à 6 chiffres t’est envoyé à{' '}
              <b>{session.email}</b>. Un lien de connexion reçu par e-mail vaut déjà preuve de
              l’adresse : il n’en redemande pas.
            </div>
            <Inp
              label="Mot de passe actuel, pour désactiver"
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
            />
            <Btn
              label={envoi ? 'Un instant…' : 'Désactiver la double authentification'}
              color={C.red}
              ghost
              full
              disabled={envoi || !motDePasse}
              onClick={desactive}
            />
          </>
        )}

        {active === false && !defi && (
          <>
            <div style={{ marginBottom: 10 }}>
              Même si quelqu’un connaît ton mot de passe, il ne pourra pas entrer sans le code
              envoyé à <b>{session.email}</b>. Pour vérifier que cette adresse reçoit bien le
              courrier, un premier code te sera demandé tout de suite.
            </div>
            <Inp
              label="Mot de passe actuel"
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
            />
            <Btn
              label={envoi ? 'Envoi du code…' : 'Activer par e-mail'}
              color={C.teal}
              full
              disabled={envoi || !motDePasse}
              onClick={demandeActivation}
            />
          </>
        )}

        {defi && (
          <>
            <Inp
              label="Code reçu par e-mail"
              placeholder="6 chiffres"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, LONGUEUR_CODE))}
            />
            <Btn
              label={envoi ? 'Vérification…' : 'Confirmer l’activation'}
              color={C.teal}
              full
              disabled={envoi || code.length !== LONGUEUR_CODE}
              onClick={confirme}
            />
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  setDefi('');
                  setCode('');
                  setInfo('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.muted,
                  fontSize: 8.5,
                  cursor: 'pointer',
                }}
              >
                Annuler
              </button>
            </div>
          </>
        )}

        {active === null && !erreur && <div style={{ color: C.muted }}>Lecture de l’état…</div>}

        {erreur && (
          <div role="alert" style={{ fontSize: 10, color: C.red, fontWeight: 700, marginTop: 8 }}>
            {erreur}
          </div>
        )}
        {info && (
          <div
            role="status"
            style={{ fontSize: 10, color: C.green, fontWeight: 700, marginTop: 8 }}
          >
            {info}
          </div>
        )}
      </div>
    </Card>
  );
}
